import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Loader } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Planos() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, session } = useAuth();

  // Verificar se há parâmetros de sucesso ou cancelamento na URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("success") === "true") {
      toast({
        title: "Assinatura realizada com sucesso!",
        description: "Sua assinatura foi processada com sucesso.",
      });
    } else if (params.get("canceled") === "true") {
      toast({
        variant: "destructive",
        title: "Assinatura cancelada",
        description: "O processo de assinatura foi cancelado.",
      });
    }
  }, [location.search, toast]);

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      
      console.log("=== INICIANDO PROCESSO DE CHECKOUT ===");
      console.log("Usuário:", user?.email || "Não logado");
      console.log("Session disponível:", !!session);
      console.log("Access token disponível:", !!session?.access_token);
      
      // Verificar se o usuário está autenticado
      if (!user || !session) {
        console.log("❌ Usuário não autenticado, redirecionando para login");
        toast({
          variant: "destructive",
          title: "Não autenticado",
          description: "Você precisa estar logado para assinar um plano.",
        });
        setIsLoading(false);
        navigate("/auth");
        return;
      }
      
      console.log("✅ Usuário autenticado, prosseguindo com checkout");
      console.log("Dados do usuário:", {
        id: user.id,
        email: user.email,
        tokenLength: session.access_token?.length || 0
      });
      
      // Verificar se a função existe fazendo uma chamada de teste
      console.log("🔄 Testando conexão com Edge Function...");
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: {
          userId: user.id,
          userEmail: user.email,
          planType: 'equipes'
        }
      });
      
      console.log("📥 Resposta da Edge Function:");
      console.log("Data:", data);
      console.log("Error:", error);
      
      if (error) {
        console.error("❌ Erro da Edge Function:", error);
        
        // Verificar tipos específicos de erro
        if (error.message?.includes('not found') || error.message?.includes('404')) {
          throw new Error("Função de checkout não encontrada. Verifique se a Edge Function está deployada no Supabase.");
        } else if (error.message?.includes('unauthorized') || error.message?.includes('401')) {
          throw new Error("Erro de autorização. Faça logout e login novamente.");
        } else {
          throw new Error(error.message || 'Erro desconhecido na função de checkout');
        }
      }

      if (data?.url) {
        console.log("✅ URL de checkout recebida:", data.url);
        console.log("🔄 Redirecionando para Stripe...");
        
        // Redirecionar para o Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error("❌ URL de checkout não recebida");
        console.log("Dados recebidos:", data);
        throw new Error("URL de checkout não foi retornada pela função");
      }
      
    } catch (error) {
      console.error("💥 ERRO COMPLETO:", error);
      console.error("Tipo do erro:", typeof error);
      console.error("Stack trace:", error instanceof Error ? error.stack : 'Não disponível');
      
      let mensagem = "Não foi possível iniciar o checkout. Tente novamente.";
      
      if (error instanceof Error) {
        console.log("Mensagem do erro:", error.message);
        mensagem = error.message;
      }
      
      toast({
        variant: "destructive",
        title: "Erro no Checkout",
        description: mensagem,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold mb-4">Planos de Assinatura</h1>
        <p className="text-lg text-gray-600">
          Escolha o plano perfeito para as necessidades da sua equipe
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {/* Plano Gratuito */}
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold">Gratuito</h2>
              <p className="text-gray-600 mt-2">Para experimentar todas as funcionalidades</p>
            </div>
            <div className="text-4xl font-bold">R$ 0,00</div>
            <ul className="space-y-4">
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Todas as features</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Limite de Diagramas</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>1 usuário</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Suporte comunitário</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Integrações Básicas</span>
              </li>
            </ul>
          </div>
        </Card>

        {/* Plano Pro */}
        <Card className="p-6 relative border-primary">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">
              Mais Popular
            </span>
          </div>
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold">Pro</h2>
              <p className="text-gray-600 mt-2">Para equipes em crescimento</p>
            </div>
            <div className="text-4xl font-bold">R$ 50,00<span className="text-lg text-gray-600">/mês</span></div>
            <ul className="space-y-4">
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>5 usuários inclusos</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Diagramas ilimitados</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Colaboração em equipe</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Suporte prioritário</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Integrações avançadas</span>
              </li>
            </ul>
            <Button 
              className="w-full" 
              onClick={handleSubscribe}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {isLoading ? "Processando..." : "Assinar"}
            </Button>
          </div>
        </Card>

        {/* Plano Empresarial */}
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold">Empresarial</h2>
              <p className="text-gray-600 mt-2">Para organizações com necessidades complexas</p>
            </div>
            <div className="text-4xl font-bold">Consulte</div>
            <ul className="space-y-4">
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Totalmente customizável</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Tudo ilimitado</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Suporte dedicado</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Integrações personalizadas</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-primary" />
                <span>Opção on-premise</span>
              </li>
            </ul>
            <Button variant="secondary" className="w-full">Entre em Contato</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
